import { Injectable } from '@angular/core';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { DrillsService } from 'src/app/modules/core/services/parameter/drills/drills.service';
import { IShift } from 'src/app/modules/core/interfaces';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { IUser } from 'projects/common/src/app/interfaces';
import { Observable } from 'rxjs';
import { IDrillsWidget } from 'src/app/modules/widget/interfaces/drills-widget';
import { IFront } from 'src/app/modules/widget/interfaces/front';
import { IFrontGroup } from 'src/app/modules/widget/interfaces/front-group';
import { momentInstance } from 'src/app/modules/core/services/datetime.service';
import { environment } from 'src/environments/environment';
import moment from 'moment';
import { IDrillMachine } from 'src/app/modules/widget/interfaces/drills-machine';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { CacheService } from '../../../../../../projects/common/src/app/services/cache.service';

@Injectable({
  providedIn: 'root'
})
export class DrillsMachineService {

  constructor(
    private parameterHandlerService: ParameterHandlerService,
    private drillsService: DrillsService,
    private cacheService: CacheService
  ) { }


  /**
   * Function to get the drills by machines
   */
  public getDrillsByMachine(): Observable<any> {
    const currentMachine = (this.parameterHandlerService.parameters.item('machine-context').value as IMachineContext).name;
    const machineCollection = this.parameterHandlerService.parameters.item('machine-collection').value as IMachineType[];
    const machine = machineCollection.find((value) => String(value.name).toLowerCase() === String(currentMachine).toLowerCase());

    const params = {
      inputDate: this.parameterHandlerService.parameters.item('date').value as string,
      shiftId: (this.parameterHandlerService.parameters.item('shift').value as IShift).shiftId,
      machineTypeId: machine.machineTypeId,
      companyId: (this.parameterHandlerService.parameters.item('user-data').value as IUser).company.id,
      zoneId: this.cacheService.session('selectedZone').zoneId
    };

    return this.drillsService.getAllStatus(params);
  }

  /**
   * Function to get the machines in front
   */
  public getFrontMachines(): Observable<any> {
    const params = {
      inputDate: this.parameterHandlerService.parameters.item('date').value as string,
      shiftId: (this.parameterHandlerService.parameters.item('shift').value as IShift).shiftId,
      companyId: (this.parameterHandlerService.parameters.item('user-data').value as IUser).company.id,
      zoneId: this.cacheService.session('selectedZone').zoneId,
    };

    return this.drillsService.getMachinesInFront(params);
  }

  /**
   * Function to group drills by machine
   * @param machines Drills of machines
   */
  public groupDrillByMachine(machines: IDrillsWidget[]): any[] {
    const drills: IDrillMachine[] = [];

    // Get all machine id and create an array with unique ids
    let machinesId = machines.map((value) => value.machineId);
    machinesId = machinesId.filter((v, index) => machinesId.indexOf(v) === index);

    let i = 0;
    for (const machineId of machinesId) {

      // Create a drill group object
      const filtered = machines.filter((value) => value.machineId === machineId);
      drills.push({
        companyId: filtered[0].companyId,
        machineId: filtered[0].machineId,
        machineModelName: filtered[0].machineModelName,
        machineName: filtered[0].machineName,
        drills: [],
        automaticDrill: 0,
        manualDrill: 0
      });

      for (const drill of filtered) {
        // Push the drills of machines by machine id
        drills[i].drills.push({
          drillDepth: drill.drillDepth,
          drillColor: drill.drillStatusColor,
          drillOperationTypeId: drill.drillOperationTypeId,
          drillOperationName: drill.drillOperationTypeName,
          shiftId: drill.shiftId,
          shiftName: drill.shiftName,
          finishedTime: drill.finishedAt,
          startedTime: drill.startedAt
        });

        if (drill.drillOperationTypeId === '2') {
          drills[i].automaticDrill = drills[i].automaticDrill + drill.drillDepth;
        } else {
          drills[i].manualDrill = drills[i].manualDrill + drill.drillDepth;
        }
      }

      i++;
    }

    const series = this.generateDrillDepthSerie(drills);
    return series;
  }


  /**
   * Function to group zones
   * @param fronts Fronts
   */
  public groupZonesByFront(fronts: IFront[]): IFrontGroup[] {

    // Get all zones and create a new object with grouped zones
    let zones = fronts.map((value) => value.zoneName);
    zones = zones.filter((v, index) => zones.indexOf(v) === index) as string[];

    const groupFront: IFrontGroup[] = [];

    let i = 0;
    for (const zone of zones) {
      // Create a front group object
      const filtered = fronts.filter((value) => value.zoneName === zone);
      groupFront.push({
        orientation: filtered[0].zoneOrientation === 'S' ? 'south' : 'north',
        status: Boolean(filtered[0].zoneStatus),
        totalDepth: Number(filtered.reduce((a, b) => a + (b.totalDepth || 0), 0).toFixed(2)),
        zoneName: filtered[0].zoneName,
        machines: []
      });

      for (const front of filtered) {

        const shiftId = (this.parameterHandlerService.parameters.item('shift').value as IShift).shiftId;

        // Time format by data
        const format = `${environment.dateFormatSystem} ${environment.timeFormat}`;
        const startDate = momentInstance(front.lastActivityStart, format);
        const finishDate = momentInstance(front.lastActivityEnd, format);

        const diff = moment.duration(startDate.diff(finishDate));

        const shiftIndex = shiftId !== -1 ? 0 : (front.shiftName === 'A' ? 0 : 1);

        if (!groupFront[i].machines[shiftIndex]) {
          groupFront[i].machines[shiftIndex] = [];
        }

        groupFront[i].machines[shiftIndex].push({
          depth: front.totalDepth,
          firstActivity: front.lastActivityStart,
          lastActivity: front.lastActivityEnd,
          machineName: front.machineName,
          shiftId: front.machineId,
          shiftName: front.shiftName,
          statusName: front.statusName,
          color: front.color,
          timeDiff: `${diff.hours()}h ${String(diff.minutes()).substr(1)}m`
        });
      }

      i++;
    }

    return groupFront;
  }


  /**
   * Function to create the drill widget serie
   * @param machines Machines with drills
   */
  private generateDrillDepthSerie(machines: IDrillMachine[]): any[] {
    const series: any[] = [];
    let machineCount = 0;

    for (const machine of machines) {
      let i = 0;
      for (const drill of machine.drills) {

        let serieData = [];

        if (series.length > 0) {
          serieData = series[i] ? series[i].data : [];
        }

        // Complete the previous machine with data 0
        for (let c = 0; c < machineCount; c ++) {
          if (!serieData || serieData.length === 0) {
            serieData.push({
              y: 0,
              extraData: {
                init: drill.startedTime,
                finish: drill.finishedTime,
                shiftName: drill.shiftName,
              }
            });
          }
        }

        // Create new drill value with time
        serieData.push({
          y: drill.drillDepth,
          extraData: {
            init: drill.startedTime,
            finish: drill.finishedTime,
            shiftName: drill.shiftName,
          }
        });


        const serie = {
          name: drill.drillOperationName,
          data: serieData,
          color: drill.drillColor,
          showInLegend: false,
          tooltip: {
            pointFormatter(): string {
              // Time format by data
              const format = `${environment.dateFormatSystem} ${environment.timeFormat}`;
              const startDate = momentInstance(this.options.extraData.init, format);
              const finishDate = momentInstance(this.options.extraData.finish, format);

              const diff = moment.duration(startDate.diff(finishDate));

              return `<strong>${this.series.name}</strong> <br>
                      <span>Turno ${this.options.extraData.shiftName}</span> <br>
                      <span>Perforación: ${this.y}mts</span> <br>
                      <span>Tiempo: ${diff.hours()}h${String(diff.minutes()).substr(1)}m
                      (${startDate.format('HH:mm')} - ${finishDate.format('HH:mm')})</span>
                      `;
            }
          }
        };

        series[i] = serie;
        i ++;
      }

      machineCount ++;
    }
    return series;
  }
}
